/**
 * The site's reference grid: a named coordinate frame laid over the ground.
 *
 * ## Why this exists as a domain module rather than as part of the overlay
 *
 * The grid is drawn on the canvas, but its *value* is that it gives the designer and the wand a
 * shared vocabulary — "the bed in C4" is a thing both can say, where "(−7.5, 2.5)" is a thing only
 * one of them says naturally. That vocabulary has to be identical in three places at once: the labels
 * painted into the scene, the text the wand's prompt receives, and any future search or import that
 * names a cell. So the naming rule lives here, as pure arithmetic over a spec, and everything else
 * asks this module rather than re-deriving it.
 *
 * ## The convention
 *
 * Cells tile from the north-west corner of the site, which is the corner a plan is read from.
 * Columns are letters running west → east (+X); rows are numbers running north → south (+Z, since
 * −Z is north). So `A1` is the north-west cell and the labels read like a street map or a chess
 * board. The origin stays at the centre of the site — the grid names points, it does not move them.
 *
 * Pure: no Three.js, no DOM, no clock. Every function is a value in, value out.
 */

/** Grid spacings offered in the UI, metres. */
export const GRID_SPACINGS: readonly number[] = [1, 2, 5, 10, 20];

/** How wide a cell must be before it is worth labelling — below this the letters collide. */
const MIN_LABELLED_SPACING_METERS = 1;

export interface GridSpec {
  /** Cell size in metres. Square cells; a rectangular grid is a rectangle of square cells. */
  spacingMeters: number;
  /** Site extent the grid covers. The grid never extends past the ground. */
  widthMeters: number;
  lengthMeters: number;
}

export interface GridCell {
  /** 0-based, west → east. */
  column: number;
  /** 0-based, north → south. */
  row: number;
  /** The name a person uses: `A1`, `C4`, `AA12`. */
  label: string;
  /** World centre of the cell, metres. */
  centerX: number;
  centerZ: number;
}

/**
 * Choose a spacing that gives a readable number of cells for a site of this size.
 *
 * Readability, not roundness, is the criterion: a 10 m site on a 10 m grid is one cell and names
 * nothing, and a 200 m site on a 1 m grid is a fog of labels. Aiming for roughly a dozen columns
 * across the longer side keeps a cell reference about as precise as a person pointing.
 */
export function defaultGridSpacing(widthMeters: number, lengthMeters: number): number {
  const span = Math.max(widthMeters, lengthMeters);
  const target = span / 12;
  let best = GRID_SPACINGS[0] ?? 5;
  for (const spacing of GRID_SPACINGS) {
    if (Math.abs(spacing - target) < Math.abs(best - target)) best = spacing;
  }
  return best;
}

export function createGridSpec(widthMeters: number, lengthMeters: number, spacingMeters?: number): GridSpec {
  return {
    widthMeters,
    lengthMeters,
    spacingMeters: spacingMeters ?? defaultGridSpacing(widthMeters, lengthMeters),
  };
}

/** Number of columns (west → east). The last one may be a partial cell at the site edge. */
export function gridColumns(spec: GridSpec): number {
  return Math.max(1, Math.ceil(round(spec.widthMeters / spec.spacingMeters, 6)));
}

/** Number of rows (north → south). */
export function gridRows(spec: GridSpec): number {
  return Math.max(1, Math.ceil(round(spec.lengthMeters / spec.spacingMeters, 6)));
}

/** Whether cells are large enough that painting a label in each is legible. */
export function gridIsLabelled(spec: GridSpec): boolean {
  return spec.spacingMeters >= MIN_LABELLED_SPACING_METERS;
}

/**
 * Spreadsheet-style column name: A…Z, then AA…AZ, BA… — so a grid wider than 26 columns keeps
 * naming cells instead of running out of letters.
 */
export function columnLabel(column: number): string {
  let index = Math.max(0, Math.trunc(column));
  let label = '';
  do {
    label = String.fromCharCode(65 + (index % 26)) + label;
    index = Math.floor(index / 26) - 1;
  } while (index >= 0);
  return label;
}

/** Inverse of {@link columnLabel}. Returns `-1` for anything that is not a column name. */
export function columnIndex(label: string): number {
  const text = label.trim().toUpperCase();
  if (!/^[A-Z]+$/.test(text)) return -1;
  let index = 0;
  for (const character of text) {
    index = index * 26 + (character.charCodeAt(0) - 64);
  }
  return index - 1;
}

/** The cell a world point falls in. Points outside the site clamp to the nearest edge cell. */
export function cellAt(spec: GridSpec, x: number, z: number): GridCell {
  const column = clamp(
    Math.floor((x + spec.widthMeters / 2) / spec.spacingMeters),
    0,
    gridColumns(spec) - 1,
  );
  const row = clamp(
    Math.floor((z + spec.lengthMeters / 2) / spec.spacingMeters),
    0,
    gridRows(spec) - 1,
  );
  return cell(spec, column, row);
}

/** The cell at a column/row index, or `null` when the index is off the grid. */
export function cellByIndex(spec: GridSpec, column: number, row: number): GridCell | null {
  if (column < 0 || row < 0 || column >= gridColumns(spec) || row >= gridRows(spec)) return null;
  return cell(spec, column, row);
}

/** Resolve a cell name (`C4`, case-insensitive) against a grid. `null` when it names no cell. */
export function cellByLabel(spec: GridSpec, label: string): GridCell | null {
  const match = label.trim().match(/^([A-Za-z]+)\s*(\d+)$/);
  if (!match) return null;
  const column = columnIndex(match[1] ?? '');
  const row = Number.parseInt(match[2] ?? '', 10) - 1;
  if (column < 0 || !Number.isFinite(row)) return null;
  return cellByIndex(spec, column, row);
}

/**
 * The cell's extent in world metres, clipped to the site so an edge cell never reports ground that
 * does not exist. A caller placing something "in C4" wants the part of C4 that is on the site.
 */
export function cellBounds(
  spec: GridSpec,
  cellOrColumn: GridCell | number,
  row?: number,
): { x0: number; x1: number; z0: number; z1: number } {
  const column = typeof cellOrColumn === 'number' ? cellOrColumn : cellOrColumn.column;
  const rowIndex = typeof cellOrColumn === 'number' ? (row ?? 0) : cellOrColumn.row;
  const halfW = spec.widthMeters / 2;
  const halfL = spec.lengthMeters / 2;
  return {
    x0: -halfW + column * spec.spacingMeters,
    x1: Math.min(halfW, -halfW + (column + 1) * spec.spacingMeters),
    z0: -halfL + rowIndex * spec.spacingMeters,
    z1: Math.min(halfL, -halfL + (rowIndex + 1) * spec.spacingMeters),
  };
}

function cell(spec: GridSpec, column: number, row: number): GridCell {
  const bounds = cellBounds(spec, column, row);
  return {
    column,
    row,
    label: `${columnLabel(column)}${row + 1}`,
    centerX: round((bounds.x0 + bounds.x1) / 2, 3),
    centerZ: round((bounds.z0 + bounds.z1) / 2, 3),
  };
}

/**
 * The grid as the rule needed to use it, for the wand's prompt.
 *
 * States the arithmetic rather than enumerating the cells: a 12 × 12 grid is 144 rows of text that
 * say the same thing a formula says in one, and the formula also covers the cells a bigger site
 * would have. The clicked cell is worked through separately by the caller as the example.
 */
export function describeGrid(spec: GridSpec): string {
  const columns = gridColumns(spec);
  const rows = gridRows(spec);
  const last = `${columnLabel(columns - 1)}${rows}`;
  return (
    `A ${spec.spacingMeters} m reference grid is switched on and visible on the designer's screen, ` +
    `so they may name places by cell instead of by coordinate.\n` +
    `${columns} columns (letters, west→east) x ${rows} rows (numbers, north→south), ` +
    `A1 at the north-west corner, ${last} at the south-east.\n` +
    `Cell centre from a name: x = ${fmt(-spec.widthMeters / 2)} + (column - 0.5) * ${spec.spacingMeters}, ` +
    `z = ${fmt(-spec.lengthMeters / 2)} + (row - 0.5) * ${spec.spacingMeters}, ` +
    `where column A = 1, B = 2, … and row 1 is the northernmost.\n` +
    `Cells are ${spec.spacingMeters} m square, so "in C4" means a ${spec.spacingMeters} m area around ` +
    `that centre — not a point. Emit world metres in ops; never emit a cell name as a coordinate.`
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round(value: number, places: number): number {
  return Math.round(value * 10 ** places) / 10 ** places;
}

function fmt(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}
